/**
 * A network diagram drawn by a user and stored server-side.
 *
 * Topologies used to be hand-authored JSON shipped with the application. They are now
 * created in the browser, owned by whoever drew them, and shared read-only — so the page
 * can describe any pipeline or cable without a developer editing a file.
 */

export type CyberNodeKind =
  | 'gateway'
  | 'controller'
  | 'workstation'
  | 'leaf'
  | 'attacker'
  | 'external';

export const CYBER_NODE_KINDS: readonly CyberNodeKind[] = [
  'gateway',
  'controller',
  'workstation',
  'leaf',
  'attacker',
  'external',
];

export interface CyberTopologyNode {
  /** Stable identifier inside the diagram, e.g. `PLC1`. Referenced by edges. */
  nodeKey: string;
  label: string;
  /** Secondary line under the label — an IP, a role, whatever the author wants shown. */
  sub: string | null;
  kind: CyberNodeKind;
  /** Layout coordinates in the author's own space; the SVG normalises them. */
  x: number;
  y: number;
  /** Set on `leaf` nodes: the `nodeKey` of the device they hang off. */
  parentKey: string | null;
  /**
   * Extra strings that also identify this node inside an alert explanation. This is what
   * connects the drawing to the alert stream — without aliases, a detector naming a device
   * by its IP will never light up the box drawn for it.
   */
  aliases: string[];
}

export interface CyberTopologyEdge {
  sourceKey: string;
  targetKey: string;
  proto: string | null;
}

export interface CyberTopologyShare {
  id: number;
  subjectType: 'USER' | 'ORGANIZATION';
  subjectId: string;
  subjectName: string | null;
}

/** One row of the topology picker. */
export interface CyberTopologySummary {
  id: number;
  name: string;
  description: string | null;
  siteId: string | null;
  ownerUserId: string;
  ownerUsername: string | null;
  /** True when the current user may edit — the owner, never a share recipient. */
  owned: boolean;
  nodeCount: number;
  updatedAt: string;
}

export interface CyberTopology {
  id: number;
  name: string;
  description: string | null;
  siteId: string | null;
  ownerUserId: string;
  ownerUsername: string | null;
  owned: boolean;
  nodes: CyberTopologyNode[];
  edges: CyberTopologyEdge[];
  /** Populated for the owner only. */
  shares: CyberTopologyShare[];
  createdAt: string;
  updatedAt: string;
}

/** What the editor submits. The whole graph is always sent; the server replaces it. */
export interface CyberTopologyDraft {
  name: string;
  description: string | null;
  siteId: string | null;
  nodes: CyberTopologyNode[];
  edges: CyberTopologyEdge[];
}

/** An empty diagram, so a new topology starts from something the editor can render. */
export function emptyDraft(siteId: string | null): CyberTopologyDraft {
  return { name: '', description: null, siteId, nodes: [], edges: [] };
}

export function toDraft(topology: CyberTopology): CyberTopologyDraft {
  return {
    name: topology.name,
    description: topology.description,
    siteId: topology.siteId,
    // Deep-copied so the editor can be cancelled without having mutated what is displayed.
    nodes: topology.nodes.map((node) => ({ ...node, aliases: [...node.aliases] })),
    edges: topology.edges.map((edge) => ({ ...edge })),
  };
}
